import React, { useEffect, useRef, useState } from 'react';
import { ACCENT, ACCENT_LIGHT, ACCENT_BORDER, TEXT_DARK, TEXT_MID, TEXT_LIGHT, FONT } from '../lib/tokens';
import { Ic } from '../components/Icons';
import { Card, ScreenWrap, SectionLabel } from '../components/ui/Primitives';
import { supabase, hasSupabase } from '../lib/supabase';
import { getAccessToken } from '../components/AccessGate';
import { useBreakpoint } from '../lib/useBreakpoint';

const BUCKET = 'uploads';

export default function UploadsScreen() {
  const fileInputRef = useRef(null);
  const [submitter, setSubmitter] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // {kind:'ok'|'err', text}
  const [approved, setApproved] = useState([]);
  const [pendingMine, setPendingMine] = useState([]);
  // 'file' = upload a document; 'link' = submit a URL. Different submission
  // path, same approval queue + downstream RAG ingestion.
  const [mode, setMode] = useState('file');
  const [linkUrl, setLinkUrl] = useState('');

  const loadApproved = async () => {
    if (!hasSupabase) return;
    const { data } = await supabase
      .from('uploads')
      .select('*')
      .eq('approved', true)
      .order('created_at', { ascending: false });
    setApproved(data || []);
  };

  useEffect(() => {
    loadApproved();
  }, []);

  const upload = async (file) => {
    if (!file || !hasSupabase) return;
    if (!submitter.trim()) {
      setStatus({ kind: 'err', text: 'Please enter your name first.' });
      return;
    }
    setBusy(true);
    setStatus(null);
    const safeName = file.name.replace(/[^\w.\-]+/g, '_');
    const path = `attendee-uploads/${Date.now()}-${safeName}`;
    const { error: storageError } = await supabase.storage.from(BUCKET).upload(path, file, {
      cacheControl: '3600',
      upsert: false,
    });
    if (storageError) {
      setBusy(false);
      setStatus({ kind: 'err', text: storageError.message || 'Upload failed.' });
      return;
    }
    // Note: don't chain .select() here — the row is approved=false, and the
    // table's SELECT policy only matches approved rows, so PostgREST would
    // reject the return-representation step even though the INSERT itself is
    // allowed. The optimistic local row is good enough for the pending list.
    const { error: dbError } = await supabase.from('uploads').insert({
      file_name: file.name,
      storage_path: path,
      uploaded_by: submitter.trim(),
      approved: false,
    });
    setBusy(false);
    if (dbError) {
      setStatus({ kind: 'err', text: dbError.message || 'Could not record upload.' });
      return;
    }
    setPendingMine((p) => [
      {
        id: `local-${Date.now()}`,
        file_name: file.name,
        uploaded_by: submitter.trim(),
        approved: false,
      },
      ...p,
    ]);
    setStatus({ kind: 'ok', text: 'Uploaded! Awaiting admin approval before it appears in the library.' });
    setTimeout(() => setStatus(null), 4000);
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = '';
  };

  // Submit a URL the AI should crawl when admin approves. We don't fetch the
  // page client-side — that happens server-side during admin approval where
  // we have full Node fetch + jsdom + Readability available.
  const submitLink = async () => {
    if (!hasSupabase) return;
    if (!submitter.trim()) {
      setStatus({ kind: 'err', text: 'Please enter your name first.' });
      return;
    }
    let normalized = linkUrl.trim();
    if (!normalized) {
      setStatus({ kind: 'err', text: 'Please paste a URL.' });
      return;
    }
    // Allow "www.example.com" or "example.com/page" inputs.
    if (!/^https?:\/\//i.test(normalized)) {
      normalized = `https://${normalized}`;
    }
    let parsed;
    try {
      parsed = new URL(normalized);
    } catch {
      setStatus({ kind: 'err', text: 'That doesn\'t look like a valid URL.' });
      return;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      setStatus({ kind: 'err', text: 'Only http(s) links are allowed.' });
      return;
    }
    setBusy(true);
    setStatus(null);
    // file_name starts as the URL — admin approval will overwrite it with
    // the page's <title> tag if Readability finds one.
    const initialName = `${parsed.hostname}${parsed.pathname !== '/' ? parsed.pathname : ''}`;
    const { error: dbError } = await supabase.from('uploads').insert({
      file_name: initialName,
      storage_path: null,
      link_url: parsed.toString(),
      uploaded_by: submitter.trim(),
      approved: false,
    });
    setBusy(false);
    if (dbError) {
      setStatus({ kind: 'err', text: dbError.message || 'Could not record link.' });
      return;
    }
    setPendingMine((p) => [
      {
        id: `local-${Date.now()}`,
        file_name: initialName,
        link_url: parsed.toString(),
        uploaded_by: submitter.trim(),
        approved: false,
      },
      ...p,
    ]);
    setLinkUrl('');
    setStatus({
      kind: 'ok',
      text: 'Submitted! Awaiting admin approval before the AI can use it.',
    });
    setTimeout(() => setStatus(null), 4000);
  };

  const handleOpen = async (item) => {
    // Links open in a new tab. Files go through /api/upload-url to mint a
    // short-lived signed download URL because the storage bucket is private.
    if (item.link_url) {
      window.open(item.link_url, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const accessToken = getAccessToken();
      const res = await fetch('/api/upload-url', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ id: item.id }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { url } = await res.json();
      if (!url) throw new Error('No URL returned');
      // <a download> survives iOS standalone PWA mode where window.open does not.
      const a = document.createElement('a');
      a.href = url;
      a.download = item.file_name || '';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      setStatus({ kind: 'err', text: `Could not download ${item.file_name}: ${e.message}` });
      setTimeout(() => setStatus(null), 4000);
    }
  };

  const bp = useBreakpoint();
  const isDesktop = bp === 'desktop';

  const uploadCard = (
    <Card style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_DARK }}>
        Share with the Group
      </div>
      <div style={{ fontSize: 12.5, color: TEXT_MID, lineHeight: 1.5 }}>
        Upload a document or paste a link to an article or web page. After an
        organizer approves it, the AI can use it to answer questions, and the
        whole group sees it in the library below.
      </div>

      {/* Mode toggle — file vs link. Same approval queue, different inputs. */}
      <div
        style={{
          display: 'flex',
          background: '#f1f5f9',
          borderRadius: 10,
          padding: 3,
          gap: 3,
        }}
      >
        <ModeBtn label="Upload a file" active={mode === 'file'} onClick={() => setMode('file')} />
        <ModeBtn label="Submit a link" active={mode === 'link'} onClick={() => setMode('link')} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <label
          style={{ fontSize: 11.5, fontWeight: 600, color: TEXT_MID, letterSpacing: '0.04em' }}
        >
          Your Name
        </label>
        <input
          type="text"
          value={submitter}
          onChange={(e) => setSubmitter(e.target.value)}
          placeholder="e.g. Marc Abla"
          style={{
            border: '1.5px solid #e2e8f0',
            borderRadius: 8,
            padding: '9px 11px',
            fontSize: 13.5,
            color: TEXT_DARK,
            fontFamily: FONT,
            outline: 'none',
            background: '#fff',
          }}
        />
      </div>

      {mode === 'file' ? (
        <>
          <input ref={fileInputRef} type="file" onChange={onFileChange} style={{ display: 'none' }} />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            style={primaryBtn(busy)}
          >
            <Ic.Upload size={16} color="#fff" />
            {busy ? 'Uploading…' : 'Choose File to Upload'}
          </button>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label
              style={{ fontSize: 11.5, fontWeight: 600, color: TEXT_MID, letterSpacing: '0.04em' }}
            >
              Link URL
            </label>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !busy && submitLink()}
              placeholder="https://example.org/article"
              autoComplete="off"
              style={{
                border: '1.5px solid #e2e8f0',
                borderRadius: 8,
                padding: '9px 11px',
                fontSize: 13.5,
                color: TEXT_DARK,
                fontFamily: FONT,
                outline: 'none',
                background: '#fff',
              }}
            />
            <div style={{ fontSize: 11, color: TEXT_LIGHT, marginTop: 2, lineHeight: 1.4 }}>
              The AI will read the page once it's approved. Heavy JavaScript
              pages may not extract cleanly — if you see an error, save the
              page as a PDF and upload it instead.
            </div>
          </div>
          <button onClick={submitLink} disabled={busy} style={primaryBtn(busy)}>
            <Ic.Link size={15} color="#fff" />
            {busy ? 'Submitting…' : 'Submit Link'}
          </button>
        </>
      )}

      {status && (
        <div
          style={{
            background: status.kind === 'ok' ? '#dcfce7' : '#fee2e2',
            border: `1px solid ${status.kind === 'ok' ? '#86efac' : '#fca5a5'}`,
            borderRadius: 9,
            padding: '10px 14px',
            fontSize: 12.5,
            color: status.kind === 'ok' ? '#16a34a' : '#b91c1c',
            lineHeight: 1.45,
          }}
        >
          {status.text}
        </div>
      )}
    </Card>
  );

  const lists = (
    <>
      {pendingMine.length > 0 && (
        <>
          <SectionLabel>AWAITING APPROVAL</SectionLabel>
          {pendingMine.map((u) => {
            const isLink = !!u.link_url;
            return (
              <Card key={u.id} style={{ padding: '11px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 8,
                      background: '#fef3c7',
                      border: '1px solid #fde68a',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {isLink ? (
                      <Ic.Link size={13} color="#b45309" />
                    ) : (
                      <Ic.Upload size={14} color="#b45309" />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: TEXT_DARK,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {u.file_name}
                    </div>
                    <div style={{ fontSize: 11, color: TEXT_LIGHT, marginTop: 1 }}>
                      {isLink ? 'Link · pending approval' : 'Pending approval'}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </>
      )}

      <SectionLabel>SHARED LIBRARY · {approved.length}</SectionLabel>
      {approved.length === 0 && (
        <div
          style={{
            fontSize: 12.5,
            color: 'rgba(255,255,255,0.45)',
            padding: '14px 4px',
            textAlign: 'center',
            lineHeight: 1.5,
          }}
        >
          No approved documents or links yet. Yours will appear here once an
          organizer approves it.
        </div>
      )}
      {approved.map((u) => {
        const isLink = !!u.link_url;
        return (
          <Card key={u.id} style={{ padding: '11px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: ACCENT_LIGHT,
                  border: `1px solid ${ACCENT_BORDER}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {isLink ? (
                  <Ic.Link size={13} color={ACCENT} />
                ) : (
                  <Ic.Upload size={13} color={ACCENT} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: TEXT_DARK,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {u.file_name}
                </div>
                <div style={{ fontSize: 11, color: TEXT_LIGHT, marginTop: 1 }}>
                  {u.uploaded_by
                    ? `${isLink ? 'Link' : 'Document'} shared by ${u.uploaded_by}`
                    : isLink
                    ? 'Shared link'
                    : 'Shared document'}
                </div>
              </div>
              <button
                onClick={() => handleOpen(u)}
                style={{
                  background: ACCENT,
                  border: 'none',
                  borderRadius: 7,
                  padding: '7px 11px',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: FONT,
                }}
              >
                {isLink ? 'Open' : 'Download'}
              </button>
            </div>
          </Card>
        );
      })}
    </>
  );

  if (isDesktop) {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '380px 1fr',
          gap: 24,
          alignItems: 'start',
        }}
      >
        <div style={{ position: 'sticky', top: 0 }}>{uploadCard}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{lists}</div>
      </div>
    );
  }

  return (
    <ScreenWrap>
      {uploadCard}
      {lists}
    </ScreenWrap>
  );
}

function ModeBtn({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: '8px 0',
        background: active ? '#fff' : 'transparent',
        boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
        border: 'none',
        borderRadius: 8,
        color: active ? ACCENT : TEXT_MID,
        fontSize: 12.5,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: FONT,
        transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );
}

function primaryBtn(busy) {
  return {
    background: busy ? '#cbd5e1' : ACCENT,
    border: 'none',
    borderRadius: 9,
    padding: '11px 20px',
    color: '#fff',
    fontSize: 13.5,
    fontWeight: 600,
    cursor: busy ? 'not-allowed' : 'pointer',
    fontFamily: FONT,
    width: '100%',
    letterSpacing: '0.01em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  };
}
